import { CartItem, ProductPackage, ProductPackageProduct } from '@src/core/buyerApiClient';
import { formatDate } from '.';

export interface PriceSummary {
  total: number;
  discount?: number;
  previousPrice?: number;
}

export interface PackageGroup<T> {
  productPackage: ProductPackage;
  items: T[];
}

export const calculateItemTotalPriceSummary = (item: CartItem): PriceSummary => {
  const discountValue = item.costPreview.packageDiscountCents ?? 0;
  const total = item.costPreview.totalPriceCents - discountValue;

  return { total, discount: discountValue, previousPrice: item.costPreview.totalPriceCents };
};

export const calculatePackageTotalPriceSummary = (items: CartItem[]): PriceSummary =>
  items.reduce(
    (acc, item) => {
      const { discount, previousPrice, total } = calculateItemTotalPriceSummary(item);

      return {
        total: acc.total + total,
        discount: acc.discount ? acc.discount + (discount ?? 0) : discount,
        previousPrice: acc.previousPrice ? acc.previousPrice + (previousPrice ?? 0) : previousPrice,
      };
    },
    { total: 0 } as PriceSummary,
  );

export const formatPackageTariffs = (items: CartItem[]) =>
  items.reduce((acc, curr) => {
    for (const { tariff, quantity } of curr.tariffs) {
      const tariffName = tariff.type.name;

      acc[tariffName] = (acc[tariffName] || 0) + quantity;
    }

    return acc;
  }, {} as Record<string, number>);

interface PackageItem {
  productPackageUnitId: string | null;
  productPackageProduct?: ProductPackageProduct | null;
}

export function groupByPackageUnitId<T extends PackageItem>(items: T[]) {
  const individualItems: T[] = [];
  const packageItemsMap: { [key: string]: T[] } = {};
  const packageGroup: PackageGroup<T>[] = [];

  items.forEach((item) => {
    const unitId = item.productPackageUnitId;

    if (unitId) {
      if (!packageItemsMap[unitId]) {
        packageItemsMap[unitId] = [];
      }
      packageItemsMap[unitId].push(item);
    } else {
      individualItems.push(item);
    }
  });

  Object.values(packageItemsMap).forEach((packageItems) => {
    const productPackage = packageItems[0]!.productPackageProduct!.productPackage;

    const packageStructure = {
      productPackage: productPackage!,
      items: packageItems,
    };

    packageGroup.push(packageStructure);
  });

  return { individualItems, packageGroup };
}

export const dateBySellingMode = (
  selectedDate: string | null,
  formattedScheduleTimeMinutes: string | null,
  fallBackText = '',
) => {
  const date = selectedDate ? formatDate(selectedDate) : '';

  if (formattedScheduleTimeMinutes) {
    return date + ' - ' + formattedScheduleTimeMinutes;
  }

  return date || fallBackText;
};
